import { Injectable, OnDestroy, inject } from "@angular/core";
import {
  AcesoTvControlRequest,
  AdvancedCommand,
  AdvancedCommandRequest,
  CallTypes,
  DeviceCameraDTO,
  DeviceMicrophoneDTO,
  DeviceSpeakerDTO,
  InteractiveChannel,
  InteractiveSessionRegistrant,
  Organization,
  OrganizationFeatures,
  Tag,
  User
} from "@vp/models";
import { FeatureService } from "@vp/shared/features";
import { filterNullMap } from "@vp/shared/operators";
import { compare, propertyAtPath } from "@vp/shared/utilities";
import { createPatch } from "rfc6902";
import { EMPTY, NEVER, Observable, Subject, combineLatest, forkJoin, of, throwError } from "rxjs";
import { catchError, map, switchMap, withLatestFrom } from "rxjs/operators";
import { AdvancedCommandsService } from "./advanced-commands.service";
import { AssignOptions, AssignService } from "./assign.service";
import { VirtualCareApiService } from "./virtual-care-api.service";

export interface TranslationServiceParams {
  facilityId: string | undefined;
  pin: string | undefined;
  topic: string | undefined;
  applicationHostId: string | undefined;
  languageCode: string | undefined;
  userId: string | undefined;
}

@Injectable({
  providedIn: "root"
})
export class VirtualCareService implements OnDestroy {
  private readonly virtualCareApiService = inject(VirtualCareApiService);
  private readonly advancedCommandsService = inject(AdvancedCommandsService);
  private readonly assignService = inject(AssignService);
  private readonly featureService = inject(FeatureService);

  private readonly destroyed$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  getInteractiveSession(
    deviceUserId: string | null,
    callType: CallTypes,
    requestCall: boolean,
    caseId?: string | null | undefined,
    visitTag?: Tag | null | undefined
  ) {
    if (callType !== CallTypes.vClinicalInformatics && !deviceUserId) {
      return throwError(() => new Error("Device ID is required for this call type"));
    }
    return this.virtualCareApiService.getInteractiveSession({
      callType: callType,
      deviceId: deviceUserId,
      caseId: caseId ?? null,
      requestCall: requestCall,
      visitTagId: visitTag?.tagId ?? null,
      visitTagDisplayName: visitTag?.displayName ?? null
    });
  }

  patchInteractiveSession(original: InteractiveChannel, updated: InteractiveChannel) {
    const patchOperations = createPatch(original, updated);
    return this.virtualCareApiService.patchInteractiveSession(
      updated.interactiveChannelId,
      patchOperations
    );
  }

  generateMeetingShareLink(
    registrant: InteractiveSessionRegistrant,
    queryParams: { hostId: string; topic: string }
  ) {
    return this.virtualCareApiService.generateMeetingShareLink(registrant, queryParams);
  }

  getSessionInfo(topic: string, hostId: string) {
    return this.virtualCareApiService.getSessionInfo({
      topic,
      hostId
    });
  }

  deactivateCallLight(deviceId: string) {
    return this.virtualCareApiService.deactivateCallLight(deviceId);
  }

  requestToJoinCall(topic: string, hostId: string) {
    return this.virtualCareApiService.getInteractiveSessionJoinUrl(topic, hostId);
  }

  sendMessage(
    message: string,
    caseId?: string | null | undefined,
    deviceId?: string | null | undefined
  ) {
    if (!message) return NEVER;
    if (caseId) {
      return this.virtualCareApiService.sendPatientMessage(caseId, message);
    } else if (deviceId) {
      return this.virtualCareApiService.sendDeviceMessage(deviceId, message);
    }
    return EMPTY;
  }

  showAdvancedCommands(deviceId: string, caseId: string) {
    return this.advancedCommandsService.getDeviceCommands().pipe(
      switchMap((advancedCommands: AdvancedCommand[]) => {
        const sortedCommands = advancedCommands.sort((a, b) =>
          a.methodName < b.methodName ? -1 : 1
        );
        return this.assignService.openAdvancedCommands(sortedCommands, deviceId, caseId);
      }),
      catchError(() => {
        return throwError(() => new Error("Failed to send command"));
      })
    );
  }

  switchCamera(userId: string) {
    if (!userId) {
      return throwError(() => new Error("Device not provided"));
    }

    const assignOptions: AssignOptions = {
      title: "Off/On",
      button: "Select",
      properties: ["deviceName"],
      columnDefs: [{ key: "deviceName", width: 75, header: "Camera" }],
      excludeProperties: ["deviceId"],
      noRecords: "No cameras found.",
      selectedKey: "isSelectedDevice",
      selectedLabelKey: "deviceName"
    };

    return this.advancedCommandsService.getDeviceCameras(userId).pipe(
      map((cameras: DeviceCameraDTO[]) => {
        const uniqueCameras = cameras.filter(
          (camera, index, arr) => arr.findIndex(item => item.deviceId === camera.deviceId) === index
        );
        return uniqueCameras;
      }),
      switchMap((cameras: DeviceCameraDTO[]) =>
        this.assignService.open(cameras, assignOptions).pipe(
          filterNullMap(),
          map(({ result, muteChanged }) => ({
            camera: result as DeviceCameraDTO,
            muteChanged
          }))
        )
      ),
      switchMap(({ camera }: { camera: DeviceCameraDTO; muteChanged: boolean }) => {
        return camera.isSelectedDevice
          ? of(false)
          : this.advancedCommandsService
              .switchCamera(userId, camera)
              .pipe(
                switchMap((newCameraSelected: boolean) =>
                  newCameraSelected
                    ? of(newCameraSelected)
                    : throwError(() => new Error("advancedCommandsService.switchCamera error"))
                )
              );
      }),
      catchError(() => {
        return throwError(() => new Error("Failure attempting to switch camera"));
      })
    );
  }

  switchMicrophone(userId: string) {
    if (!userId) {
      return throwError(() => new Error("Device not provided"));
    }

    const assignOptions: AssignOptions = {
      title: "Mute",
      button: "Select",
      multiple: false,
      properties: ["deviceName"],
      columnDefs: [{ key: "deviceName", width: 75, header: "Microphone" }],
      excludeProperties: ["deviceId"],
      noRecords: "No microphones found.",
      selectedKey: "isSelectedDevice",
      selectedLabelKey: "deviceName"
    };

    return this.advancedCommandsService.getDeviceMicrophones(userId).pipe(
      map((mics: DeviceMicrophoneDTO[]) => {
        mics.forEach(mic => (mic.isMuted ? (mic.isMuted = "Yes") : (mic.isMuted = "No")));
        return mics;
      }),
      switchMap((mics: DeviceMicrophoneDTO[]) =>
        this.assignService.open(mics, assignOptions).pipe(
          filterNullMap(),
          map(({ result, muteChanged }) => ({
            mic: result as DeviceMicrophoneDTO,
            muteChanged
          }))
        )
      ),
      switchMap(({ mic, muteChanged }: { mic: DeviceMicrophoneDTO; muteChanged: boolean }) => {
        mic.isMuted === "Yes" ? (mic.isMuted = true) : (mic.isMuted = false);

        const sendCommand: AdvancedCommandRequest = {
          commandName: "toggleMicrophone",
          deviceUserId: userId
        } as AdvancedCommandRequest;

        const mute$ = muteChanged
          ? this.advancedCommandsService.sendDeviceCommand(sendCommand).pipe(
              catchError(() => {
                return throwError(
                  () => new Error("advancedCommandsService.toggleMicrophone error")
                );
              })
            )
          : of(false);

        const switchMic$ = mic.isSelectedDevice
          ? of(false)
          : this.advancedCommandsService
              .switchMic(userId, mic)
              .pipe(
                switchMap((result: boolean) =>
                  result
                    ? of(result)
                    : throwError(() => new Error("advancedCommandsService.switchMic error"))
                )
              );

        return forkJoin([mute$, switchMic$]).pipe(map(([_, switchMicResult]) => switchMicResult));
      })
    );
  }

  switchSpeaker(userId: string) {
    if (!userId) {
      return throwError(() => new Error("Device not provided"));
    }

    const assignOptions: AssignOptions = {
      title: "Volume",
      button: "Select",
      properties: ["deviceName"],
      columnDefs: [{ key: "deviceName", width: 75, header: "Speaker" }],
      excludeProperties: ["deviceId"],
      noRecords: "No speaker found.",
      selectedKey: "isSelectedDevice",
      selectedLabelKey: "deviceName"
    };

    return this.advancedCommandsService.getDeviceSpeakers(userId).pipe(
      map((speakers: DeviceSpeakerDTO[]) => {
        speakers.forEach(speaker => {
          speaker.isMuted ? (speaker.isMuted = "Yes") : (speaker.isMuted = "No");
          speaker.masterVolumeScalar = speaker.masterVolumeScalar;
        });
        return speakers;
      }),
      switchMap((speakers: DeviceSpeakerDTO[]) =>
        this.assignService.open(speakers, assignOptions).pipe(
          filterNullMap(),
          map(({ result, muteChanged }) => ({
            speaker: result as DeviceSpeakerDTO,
            muteChanged
          }))
        )
      ),
      switchMap(({ speaker, muteChanged }: { speaker: DeviceSpeakerDTO; muteChanged: boolean }) => {
        speaker.isMuted === "Yes" ? (speaker.isMuted = true) : (speaker.isMuted = false);
        speaker.masterVolumeScalar = speaker.masterVolumeScalar;

        if (!muteChanged && speaker.isSelectedDevice) {
          return of(false);
        }

        return this.advancedCommandsService
          .switchSpeaker(userId, speaker)
          .pipe(
            switchMap((result: boolean) =>
              result
                ? of(result)
                : throwError(() => new Error("advancedCommandsService.switchSpeaker error"))
            )
          );
      })
    );
  }

  launchEkoApplication(userId: string): Observable<void> {
    return this.advancedCommandsService
      .sendDeviceCommand({
        deviceUserId: userId,
        commandName: "launchEkoApplication"
      } as AdvancedCommandRequest)
      .pipe(map(() => undefined));
  }

  acesoTvControl(tvControlRequest: AcesoTvControlRequest) {
    return this.virtualCareApiService.acesoTvControl(tvControlRequest);
  }

  getVisitTags$(
    organization$: Observable<Organization | null>,
    tags$: Observable<Tag[]>
  ): Observable<Tag[]> {
    return combineLatest([
      organization$.pipe(filterNullMap()),
      this.featureService.configurationValue$(
        OrganizationFeatures.virtualCare,
        "visitTypeTagTypeId"
      )
    ]).pipe(
      map(([org, tagTypeId]) => org.tagTypes.find(t => t.tagTypeId === tagTypeId)),
      filterNullMap(),
      withLatestFrom(tags$),
      map(([visitTagType, tags]) =>
        tags
          .filter(tag => tag.tagTypeFriendlyId === visitTagType.friendlyId)
          .sort((a, b) => {
            const aSortOrder = propertyAtPath(a.tagData, "sortOrder");
            const bSortOrder = propertyAtPath(b.tagData, "sortOrder");

            // Handle cases where sortOrder may be null || undefined
            if (aSortOrder == null && bSortOrder != null) return 1;
            if (aSortOrder != null && bSortOrder == null) return -1;

            return (
              compare(aSortOrder, bSortOrder, true) || compare(a.displayName, b.displayName, true)
            );
          })
      )
    );
  }

  getSelectableVisitTags$(
    visitTags: Tag[],
    allTags: Tag[],
    loggedInUser: User | null,
    tagTypeFriendlyId: string
  ): Tag[] {
    const selectableVisitTypeIds = allTags
      .filter(
        t =>
          loggedInUser?.assignedTags.includes(t.tagId) && t.tagTypeFriendlyId === tagTypeFriendlyId
      )
      .flatMap(t => propertyAtPath(t.tagData, "selectableVisitTypes") ?? []);

    return visitTags.filter(tag => selectableVisitTypeIds.includes(tag.tagId));
  }
}
